import { useEffect, useState } from 'react';

// Safari's toolbar collapsing shrinks the visual viewport too, by less than
// this; only the keyboard takes more.
const KEYBOARD_MIN_PX = 150;

const NOT_TYPED_INTO = ['checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'file', 'color'];

function editableHasFocus() {
  const el = document.activeElement;
  if (!el) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  return el.tagName === 'INPUT' && !NOT_TYPED_INTO.includes(el.type);
}

/**
 * How much of the bottom of the layout viewport the on-screen keyboard covers.
 *
 * On iOS Safari the keyboard shrinks neither the layout viewport nor `dvh`,
 * so a bar pinned to `bottom: 0` ends up behind it. Sticky bars sit at
 * `bottom: inset` while `keyboardUp`, and the tab bar hides.
 *
 * `keyboardUp` needs all three: the visual viewport is more than 150px
 * shorter than the window, it is not pinch-zoomed (zoom shrinks it too), and
 * a field that takes typing has focus.
 *
 * `enabled: false` stops listening (a closed sheet), and reads no keyboard.
 *
 * @returns {{ inset: number, keyboardUp: boolean }}
 */
export default function useKeyboardInset(enabled = true) {
  const [state, setState] = useState({ inset: 0, keyboardUp: false });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!enabled) {
      setState((prev) => (prev.inset === 0 && !prev.keyboardUp ? prev : { inset: 0, keyboardUp: false }));
      return undefined;
    }
    if (!vv) return undefined;
    const update = () => {
      const shrink = window.innerHeight - vv.height;
      const keyboardUp = shrink > KEYBOARD_MIN_PX
        && Math.abs(vv.scale - 1) < 0.01
        && editableHasFocus();
      const inset = keyboardUp ? Math.max(0, Math.round(shrink - vv.offsetTop)) : 0;
      setState((prev) => (
        prev.inset === inset && prev.keyboardUp === keyboardUp ? prev : { inset, keyboardUp }
      ));
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    // Focus can move between a field and a button with no resize in between.
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, [enabled]);

  return state;
}
